/**
 * PatientHep — patient-facing Home Exercise Program.
 * Iter 68.
 *
 * Shows the patient their assigned HEP videos with a "✓ Did this today" button
 * for each exercise. Logs adherence + lets the patient rate difficulty + pain.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Dumbbell, PlayCircle, CheckCircle2, Loader2, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function PatientHep() {
  const [programs, setPrograms] = useState([]);
  const [adherence, setAdherence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState({});
  const [busyKey, setBusyKey] = useState('');
  const [feedback, setFeedback] = useState({}); // {[`${pid}-${vid}`]: { difficulty, pain }}

  useEffect(() => {
    (async () => {
      try {
        const [pr, ad] = await Promise.all([
          api.get('/hep/programs/me'),
          api.get('/hep/adherence/me'),
        ]);
        setPrograms(pr.data || []);
        setAdherence(ad.data || []);
        // Pre-fetch video details
        const ids = new Set();
        (pr.data || []).forEach(p => (p.items || []).forEach(it => ids.add(it.video_id)));
        const fetched = {};
        await Promise.all([...ids].map(id =>
          api.get(`/hep/videos/${id}`).then(r => { fetched[id] = r.data; }).catch(() => {})
        ));
        setVideos(fetched);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const logDone = async (programId, videoId) => {
    const key = `${programId}-${videoId}`;
    setBusyKey(key);
    try {
      const fb = feedback[key] || {};
      const r = await api.post('/hep/adherence', {
        program_id: programId,
        video_id: videoId,
        difficulty: fb.difficulty,
        pain: fb.pain,
      });
      setAdherence(a => [r.data, ...a]);
      toast.success('Logged — keep going!');
    } catch (e) {
      toast.error('Failed to log.');
    } finally {
      setBusyKey('');
    }
  };

  const countToday = (videoId) => {
    const today = new Date().toISOString().slice(0, 10);
    return adherence.filter(a => a.video_id === videoId && (a.done_at || '').slice(0, 10) === today).length;
  };

  if (loading) return <div className="p-8">Loading your program…</div>;

  if (programs.length === 0) {
    return (
      <div className="p-4 md:p-8 max-w-4xl">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">YOUR PROGRAM</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2">Home Exercise Program</h1>
        <div className="bento-card text-center py-10" data-testid="hep-empty-state">
          <Dumbbell className="w-12 h-12 mx-auto text-fg-muted mb-3"/>
          <div className="text-fg-muted text-sm">Your clinician hasn't assigned a home program yet. They'll add one after your next visit.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">YOUR PROGRAM</div>
      <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2">Home Exercise Program</h1>
      <p className="text-fg-muted text-sm mb-6">Do the exercises below {programs[0]?.frequency_per_week || 3}× per week. Tap "Did this" each time you finish.</p>

      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="hep-summary-stats">
        <Stat icon={CheckCircle2} label="Done today" value={adherence.filter(a => (a.done_at || '').slice(0, 10) === new Date().toISOString().slice(0, 10)).length}/>
        <Stat icon={Calendar} label="This week" value={(() => {
          const wkAgo = new Date(); wkAgo.setDate(wkAgo.getDate() - 7);
          return adherence.filter(a => new Date(a.done_at) >= wkAgo).length;
        })()}/>
        <Stat icon={TrendingUp} label="All-time" value={adherence.length}/>
      </div>

      {programs.map(prog => (
        <div key={prog.program_id} className="mb-6" data-testid={`hep-program-${prog.program_id}`}>
          <h2 className="text-xl font-heading font-bold mb-3">{prog.name}</h2>
          {prog.notes && <p className="text-sm text-fg-muted mb-3">{prog.notes}</p>}
          <div className="space-y-3">
            {(prog.items || []).map(item => {
              const v = videos[item.video_id];
              if (!v) return null;
              const key = `${prog.program_id}-${item.video_id}`;
              const fb = feedback[key] || {};
              const today = countToday(item.video_id);
              return (
                <div key={item.video_id} className="bento-card" data-testid={`hep-video-${item.video_id}`}>
                  <div className="flex gap-3 mb-3">
                    {v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt="" className="w-24 h-16 object-cover rounded-md flex-shrink-0"/>
                    ) : (
                      <div className="w-24 h-16 rounded-md bg-brand/10 flex items-center justify-center flex-shrink-0">
                        <PlayCircle className="w-6 h-6 text-brand"/>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-base truncate">{v.name}</div>
                      <div className="text-xs text-fg-muted">
                        {item.sets || '?'} × {item.reps || '?'} reps · rest {item.rest_sec || 60}s
                        {item.hold_sec ? ` · hold ${item.hold_sec}s` : ''}
                      </div>
                      {today > 0 && (
                        <div className="inline-flex items-center gap-1 text-[11px] mt-1 text-[#10A862] font-semibold">
                          <CheckCircle2 className="w-3 h-3"/> Done {today}× today
                        </div>
                      )}
                    </div>
                  </div>
                  {v.video_url && (
                    <a href={v.video_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline mb-3"
                       data-testid={`hep-video-link-${item.video_id}`}>
                      <PlayCircle className="w-3.5 h-3.5"/> Watch the demo
                    </a>
                  )}
                  {item.notes && <p className="text-xs text-fg-muted mb-3 italic">"{item.notes}"</p>}

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <SliderRow label="Difficulty (0-10)" value={fb.difficulty ?? 5}
                               onChange={(v) => setFeedback(f => ({ ...f, [key]: { ...fb, difficulty: v } }))}
                               testid={`hep-difficulty-${item.video_id}`}/>
                    <SliderRow label="Pain (0-10)" value={fb.pain ?? 0}
                               onChange={(v) => setFeedback(f => ({ ...f, [key]: { ...fb, pain: v } }))}
                               testid={`hep-pain-${item.video_id}`}/>
                  </div>

                  <button onClick={() => logDone(prog.program_id, item.video_id)}
                          disabled={busyKey === key}
                          className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid={`hep-mark-done-${item.video_id}`}>
                    {busyKey === key ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                    {busyKey === key ? 'Logging…' : '✓ Did this'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bento-card text-center !p-3">
      <Icon className="w-4 h-4 mx-auto text-brand mb-1"/>
      <div className="text-2xl font-heading font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
    </div>
  );
}

function SliderRow({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-fg-muted mb-0.5">
        <span>{label}</span>
        <span className="font-bold text-brand">{value}</span>
      </div>
      <input type="range" min="0" max="10" value={value}
             onChange={(e) => onChange(parseInt(e.target.value))}
             className="w-full accent-brand"
             data-testid={testid}/>
    </div>
  );
}
